import { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import API from '../../utils/api';
import Modal from '../../components/common/Modal';
import PageHeader from '../../components/common/PageHeader';
import EmptyState from '../../components/common/EmptyState';
import { HiOutlinePlus, HiOutlinePencil, HiOutlineTrash, HiOutlineLocationMarker, HiOutlineUsers } from 'react-icons/hi';

const Sites = () => {
  const [sites, setSites] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [selectedSite, setSelectedSite] = useState(null);
  const [selectedWorkers, setSelectedWorkers] = useState([]);
  const [formData, setFormData] = useState({ name: '', location: '', description: '' });
  const { t } = useLanguage();

  useEffect(() => { fetchSites(); fetchWorkers(); }, []);

  const fetchSites = async () => {
    try { const { data } = await API.get('/admin/sites'); setSites(data.sites); }
    catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const fetchWorkers = async () => {
    try { const { data } = await API.get('/admin/workers'); setWorkers(data.workers); }
    catch (error) { console.error('Error:', error); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSite) { await API.put(`/admin/sites/${editingSite.id}`, formData); }
      else { await API.post('/admin/sites', formData); }
      setModalOpen(false); setEditingSite(null);
      setFormData({ name: '', location: '', description: '' });
      fetchSites();
    } catch (error) { alert(error.response?.data?.message || 'Error'); }
  };

  const handleAssign = async () => {
    try {
      await API.post(`/admin/sites/${selectedSite.id}/assign-workers`, { workerIds: selectedWorkers });
      setAssignModalOpen(false); fetchSites();
    } catch (error) { alert(error.response?.data?.message || 'Error'); }
  };

  const handleEdit = (site) => {
    setEditingSite(site);
    setFormData({ name: site.name, location: site.location || '', description: site.description || '' });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('আপনি কি নিশ্চিত?')) return;
    try { await API.delete(`/admin/sites/${id}`); fetchSites(); }
    catch (error) { alert(error.response?.data?.message || 'Error'); }
  };

  const openAssignModal = (site) => {
    setSelectedSite(site);
    setSelectedWorkers(site.workers.map((w) => w.id));
    setAssignModalOpen(true);
  };

  const colors = ['from-sky-500 to-blue-600', 'from-violet-500 to-purple-600', 'from-rose-500 to-pink-600', 'from-amber-500 to-orange-600', 'from-brand-500 to-emerald-600', 'from-indigo-500 to-blue-600'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="সাইট তালিকা"
        subtitle={`মোট ${sites.length} টি সাইট`}
        action={
          <button onClick={() => { setEditingSite(null); setFormData({ name: '', location: '', description: '' }); setModalOpen(true); }} data-welcome="add-site" className="btn-primary">
            <HiOutlinePlus className="w-5 h-5" />
            <span className="hidden sm:inline">{t('addSite')}</span>
          </button>
        }
      />

      {sites.length === 0 ? (
        <div className="card">
          <EmptyState
            icon="📍"
            title="কোনো সাইট নেই"
            description="প্রথম সাইট যোগ করে শুরু করুন"
            action={
              <button onClick={() => { setEditingSite(null); setFormData({ name: '', location: '', description: '' }); setModalOpen(true); }} className="btn-primary">
                <HiOutlinePlus className="w-5 h-5" /> প্রথম সাইট যোগ করুন
              </button>
            }
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {sites.map((site, i) => (
            <div key={site.id} className="card group hover:shadow-pop transition-all duration-200">
              <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${colors[i % colors.length]} flex items-center justify-center text-white text-2xl mb-3 shadow-pop`}>
                🏗️
              </div>
              <div className="flex items-start justify-between gap-2 mb-1">
                <h3 className="font-bold text-ink-900 dark:text-white text-lg truncate">{site.name}</h3>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => handleEdit(site)} className="p-1.5 text-ink-500 hover:text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-900/30 rounded-lg">
                    <HiOutlinePencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(site.id)} className="p-1.5 text-ink-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/30 rounded-lg">
                    <HiOutlineTrash className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {site.location && (
                <p className="text-sm text-ink-500 dark:text-ink-400 flex items-center gap-1.5 mb-2">
                  <HiOutlineLocationMarker className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate">{site.location}</span>
                </p>
              )}
              {site.description && (
                <p className="text-sm text-ink-600 dark:text-ink-400 line-clamp-2 mb-3">{site.description}</p>
              )}
              <div className="mt-3 pt-3 border-t border-ink-100 dark:border-ink-700 flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-xs text-ink-500 dark:text-ink-400">
                  <HiOutlineUsers className="w-4 h-4" />
                  <span className="font-semibold">{site.workers.length}</span> ওয়ার্কার
                </div>
                <button onClick={() => openAssignModal(site)} className="text-xs font-semibold text-brand-600 dark:text-brand-400 hover:text-brand-700">
                  নিয়োগ →
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editingSite ? t('editSite') : t('addSite')}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="input-label">{t('siteName')}</label>
            <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="input-field" required />
          </div>
          <div>
            <label className="input-label">{t('siteLocation')}</label>
            <input type="text" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} className="input-field" />
          </div>
          <div>
            <label className="input-label">{t('siteDescription')}</label>
            <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="input-field" rows={3} />
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={() => setModalOpen(false)} className="btn-secondary">{t('cancel')}</button>
            <button type="submit" className="btn-primary">{t('save')}</button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={assignModalOpen} onClose={() => setAssignModalOpen(false)} title={t('assignWorkers')}>
        <div className="space-y-4">
          <div className="space-y-2 max-h-72 overflow-y-auto -mx-1 px-1">
            {workers.map((worker) => (
              <label key={worker.id} className="flex items-center gap-3 p-3 hover:bg-ink-100 dark:hover:bg-ink-700/60 rounded-xl cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={selectedWorkers.includes(worker.id)}
                  onChange={(e) => {
                    if (e.target.checked) setSelectedWorkers([...selectedWorkers, worker.id]);
                    else setSelectedWorkers(selectedWorkers.filter((id) => id !== worker.id));
                  }}
                  className="w-4 h-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                />
                <div className="w-8 h-8 rounded-lg bg-gradient-brand flex items-center justify-center text-white font-bold text-xs">
                  {worker.name.charAt(0)}
                </div>
                <span className="text-sm font-medium text-ink-900 dark:text-white">{worker.name}</span>
              </label>
            ))}
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <button onClick={() => setAssignModalOpen(false)} className="btn-secondary">{t('cancel')}</button>
            <button onClick={handleAssign} className="btn-primary">{t('save')}</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Sites;
